import { useMemo } from 'react';
import { Employee, DayShift, Shift } from '@/types';
import { checkMultipleEmployeeAvailability, EmployeeAvailability } from '@/lib/scheduling';

interface UseEmployeeAvailabilityProps {
  employees: Employee[];
  proposedStartTime: Date | null;
  proposedEndTime: Date | null;
  existingShifts: (Shift | DayShift)[];
  excludeShiftId?: string;
}

interface UseEmployeeAvailabilityReturn {
  availabilityMap: Map<string, EmployeeAvailability>;
  isEmployeeAvailable: (employeeId: string) => boolean;
  getEmployeeConflicts: (employeeId: string) => EmployeeAvailability['conflictingShifts'];
}

export const useEmployeeAvailability = ({
  employees,
  proposedStartTime,
  proposedEndTime,
  existingShifts,
  excludeShiftId
}: UseEmployeeAvailabilityProps): UseEmployeeAvailabilityReturn => {
  // Calculate availability for all employees
  const availabilityMap = useMemo(() => {
    if (!proposedStartTime || !proposedEndTime) {
      return new Map<string, EmployeeAvailability>();
    }

    return checkMultipleEmployeeAvailability(
      employees,
      proposedStartTime,
      proposedEndTime,
      existingShifts,
      excludeShiftId
    );
  }, [employees, proposedStartTime, proposedEndTime, existingShifts, excludeShiftId]);

  // Helper function to check if a specific employee is available
  const isEmployeeAvailable = (employeeId: string): boolean => {
    const availability = availabilityMap.get(employeeId);
    return availability?.isAvailable ?? true;
  };

  // Helper function to get conflicts for a specific employee
  const getEmployeeConflicts = (employeeId: string): EmployeeAvailability['conflictingShifts'] => {
    const availability = availabilityMap.get(employeeId);
    return availability?.conflictingShifts ?? [];
  };

  return {
    availabilityMap,
    isEmployeeAvailable,
    getEmployeeConflicts
  };
};